import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AdminRouteContainer } from "components/general/AdminRouteContainer";
import { ModalHandler } from "components/general/ModalHandler";
import { Row, OverlayTrigger, Tooltip, Button, Form, Col } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import logo from "../../assets/logo.png";
import { faExclamationTriangle, faCheckCircle, faCircleInfo, faTimesCircle, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { ICancelContinueModal, IConfirmModal } from "interfaces/IModal";
import { useEffect, useState } from "react";
import { createAuthenticatedAxiosInstance, showSize, updateSize } from "api/auth";
import { useAuthStore } from "../../zustand/loginStore";
import { isSuccessStatusCode } from "helpers/httpResponse";
import { Size } from "interfaces/ISize";
import { FeedbackBorderContainer } from "components/general/FeedbackBorderContainer";

/**
 * The size details component
 */
export const SizeEdit = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { id } = location.state || {};
    const [size, setSize] = useState<Size>();
    const [nameInput, setNameInput] = useState<string>("");
    const [labelInput, setLabelInput] = useState<string>("");
    const [nameValidation, setNameValidation] = useState<boolean | undefined>(undefined);
    const [labelValidation, setLabelValidation] = useState<boolean | undefined>(undefined);
    const [nameMessage, setNameMessage] = useState<string>("");
    const [labelMessage, setLabelMessage] = useState<string>("");
    const { token } = useAuthStore();
    const axiosInstance = createAuthenticatedAxiosInstance(token);
    const initialModal = {
        show: false,
        onHide: () => { },
        onConfirm: () => { },
        title: "Atención",
        message: "",
        icon: faExclamationTriangle,
        iconColor: "text-danger"
    };
    const [modal, setModal] = useState<IConfirmModal | ICancelContinueModal>(initialModal);

    /**
     * Hides the modal and set the default initial modal
    */
    const onHideModal = () => setModal(initialModal);

    /**
     * on patch size modal that makes an api call to patch the current size
    */
    const onSave = async () => {
        if (!size)
            return;
        const trimmedName = nameInput.trim();
        const trimmedLabel = labelInput.trim();
        const isValid = trimmedName.length <= 50 && trimmedLabel.length <= 50;

        const { data, statusCode, result } = await updateSize(axiosInstance, id, { name: trimmedName, description: trimmedLabel });
        const successModal: IConfirmModal = {
            show: true,
            title: "Confirmación",
            message: "La talla se ha guardado con éxito.",
            icon: faCheckCircle,
            iconColor: "text-success",
            onHide: () => navigate("/inventories/sizes/details", { state: size })
        }

        const errorModal: IConfirmModal = {
            title: "Confirmación",
            message: "Error al conectar con el servidor. Inténtalo más tarde.",
            icon: faTimesCircle,
            iconColor: "text-danger",
            onHide: onHideModal,
            show: true
        }

        if (!isValid) {
            setNameValidation(trimmedName.length >= 50 ? false : undefined);
            setLabelValidation(trimmedLabel.length >= 50 ? false : undefined);
            setNameMessage("El nombre de la talla es demasiado largo. Escriba uno que tenga menos de 50 caracteres.");
            setLabelMessage("El nombre del distintivo es demasiado largo. Escriba uno que tenga menos de 50 caracteres.");
            return;
        }
        if (!isSuccessStatusCode(statusCode)) {
            const nameAlreadyTaken = result?.errors?.name;
            const descriptionAlreadyTaken = result?.errors?.description;
            if (nameAlreadyTaken || descriptionAlreadyTaken) {
                setModal(
                    {
                        ...errorModal,
                        message: `La talla no se pudo agregar: ${nameAlreadyTaken ? "\nEl nombre ya existe" : ""} ${descriptionAlreadyTaken ? "\nLa descripción ya existe" : ""}`
                    }
                );
                return;
            }
            setModal(errorModal);
            return;
        }
        setModal(successModal);
    }

    /**
     * on modal button cancel click to reset values
     */
    const onCancel = () => {
        if (!size)
            return;
        const { name, description } = size;
        setNameInput(name);
        setLabelInput(description);
        setLabelValidation(undefined);
        setNameValidation(undefined);
        setLabelMessage("");
        setNameMessage("");
        onHideModal();
        navigate("/inventories/sizes/details", { state: size });
    }

    /**
     * handles the back button click and returns to the previous route
    */
    const onCancelClick = () => {
        setModal({
            ...modal,
            message: "¿Estás seguro de cancelar la edición?\nLos cambios sin guardar no surtirán efecto.",
            show: true,
            onConfirm: onCancel,
            onHide: onHideModal
        });
    };

    /**
     * Opens the status related modal
    */
    const onSaveClick = () => {
        const trimmedName = nameInput.trim();
        const trimmedLabel = labelInput.trim();
        const isAnyFieldEmpty = trimmedName.length <= 0 && trimmedLabel.length <= 0;
        if (isAnyFieldEmpty) {
            setNameValidation(trimmedName.length >= 50 ? false : undefined);
            setLabelValidation(trimmedLabel.length >= 50 ? false : undefined);
            setNameMessage("El nombre no es válido o el campo se encuentra vacío.");
            setLabelMessage("El distintivo no es válido o el campo se encuentra vacío.");
            return;
        }
        setModal({
            ...modal,
            message: "¿Estás seguro de guardar los cambios?",
            show: true,
            onConfirm: onSave,
            onHide: onHideModal
        });
    };

    /**
     * Fetch the size information
     */
    const fetchSize = async () => {
        const { data, statusCode } = await showSize(axiosInstance, id);
        if (!isSuccessStatusCode(statusCode)) {
            return;
        }
        if (!data)
            return;
        const { name, description } = data;
        setSize(data);
        setNameInput(name);
        setLabelInput(description);
    }

    /**
     * fetch the size details are required on render
     */
    useEffect(() => {
        fetchSize();
    }, []);

    return (
        <AdminRouteContainer customClass="align-items-stretch" customMaxWidth={1000} backRoute="/inventories/sizes/details" backRouteOptions={{ state: size }}>
            <>
                {size &&
                    <>
                        <Row className="justify-content-center text-center">
                            <img
                                src={logo}
                                alt="Logo de la Boutique"
                                className="mb-4"
                                style={{ width: "215px" }}
                            />
                            <h2 className="fw-bold">Editar talla</h2>
                        </Row>
                        <Row className="d-flex flex-nowrap align-items-center mb-4 mt-5">
                            <Col xs={6}>
                                <Form.Label className="fw-bold">No.</Form.Label>
                                <div className="d-flex align-items-center">
                                    <span style={{ fontSize: "24px", color: "#747775" }}>
                                        {id}
                                    </span>
                                    <OverlayTrigger
                                        trigger={["hover", "focus"]}
                                        placement="right"
                                        overlay={
                                            <Tooltip className="custom-tooltip">
                                                Número identificador de la talla
                                            </Tooltip>
                                        }
                                    >
                                        <FontAwesomeIcon icon={faInfoCircle} className="ms-2" />
                                    </OverlayTrigger>
                                </div>
                            </Col>
                            <div className="d-flex align-items-start flex-shrink-1 justify-content-end">
                                <OverlayTrigger
                                    placement="right"
                                    overlay={
                                        <Tooltip className="custom-tooltip">
                                            Estado actual de la talla
                                        </Tooltip>
                                    }
                                >
                                    <span
                                        className={`status-badge ${size?.status ? "active" : "inactive"
                                            }`}
                                    >
                                        {size?.status ? "Activo" : "Inactivo"}
                                    </span>
                                </OverlayTrigger>
                            </div>
                        </Row>
                        <Row className="align-items-center mb-4 mt-5">
                            <Form>
                                <Form.Group className="mb-4">
                                    <Form.Label className="fw-bold">
                                        Nombre de la talla:
                                    </Form.Label>
                                    <FeedbackBorderContainer responseState={nameValidation} message={nameMessage}>
                                        <Form.Control
                                            type="text"
                                            onChange={(e) => setNameInput(e.target.value)}
                                            value={nameInput}
                                            readOnly={false}
                                            placeholder={size.name}
                                        />
                                    </FeedbackBorderContainer>
                                </Form.Group>
                                <Form.Group className="mb-4 text-center justify-items-center">
                                    <Form.Label className="fw-bold">
                                        Distintivo
                                        <OverlayTrigger
                                            placement="right"
                                            overlay={
                                                <Tooltip className="custom-tooltip">
                                                    EL valor distintivo es el que verá el cliente al momento de hacer su compra.
                                                    <br />
                                                    Ej. S, M, L o CH, M, G.
                                                </Tooltip>
                                            }
                                        >
                                            <FontAwesomeIcon icon={faCircleInfo} className="ms-2" />
                                        </OverlayTrigger>
                                    </Form.Label>
                                    <FeedbackBorderContainer responseState={labelValidation} message={labelMessage}>
                                        <Form.Control
                                            style={{ width: "20%", justifySelf: "center" }}
                                            type="text"
                                            onChange={(e) => setLabelInput(e.target.value)}
                                            value={labelInput}
                                            readOnly={false}
                                            placeholder={size.description}
                                        />
                                    </FeedbackBorderContainer>
                                </Form.Group>
                            </Form>
                        </Row>
                        <Row className="d-flex justify-content-between" >
                            <Button
                                variant="secondary"
                                type="button"
                                onClick={onCancelClick}
                                className=" fw-bold align-item-center text-center ms-2"
                                style={{ width: "fit-content" }}
                            >
                                Cancelar
                            </Button>
                            <Button
                                variant="primary"
                                type="button"
                                onClick={onSaveClick}
                                className=" fw-bold align-item-center text-center me-2"
                                style={{ width: "fit-content" }}
                            >
                                Guardar
                            </Button>
                        </Row>
                    </>
                }
                <ModalHandler modal={modal} />
            </>
        </AdminRouteContainer>
    )
}
